import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Sparkles, Handshake, Landmark, Globe, Gem, Compass } from "lucide-react";
import { FadeIn, useFadeInOnScroll } from "@/lib/hooks";
import {
    BRAND,
    IMG,
    HERITAGE_STATS,
    FEATURED_CATEGORIES,
    SIGNATURE_COLLECTIONS,
    WHY_CHOOSE_US,
} from "@/lib/data";
import { HOME } from "@/constants/testIds";
import Testimonials from "@/components/Testimonials";
import PrivateConsultation from "@/components/PrivateConsultation";
import PresenceSection from "@/components/PresenceSection";
import HeroCarousel from "@/components/HeroCarousel";

const ICONS = { sparkles: Sparkles, handshake: Handshake, landmark: Landmark, globe: Globe, gem: Gem, compass: Compass };

export default function Home() {
    useFadeInOnScroll();

    const scrollToConsult = () => {
        const el = document.getElementById("consultation");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <main data-testid={HOME.root} className="bg-[#0A0A0A] text-[#F1F1EE]">
            {/* HERO */}
            <section data-testid={HOME.hero} className="relative h-[100vh] min-h-[640px] w-full overflow-hidden bg-[#0A0A0A]">
                <HeroCarousel intervalMs={3000} transitionMs={1400} />
                <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-black/90 z-[1]" />

                {/* Big brand logo imprint on the right */}
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end pr-4 md:pr-16 z-[1]">
                    <img
                        src={BRAND.logo}
                        alt=""
                        aria-hidden="true"
                        className="w-[70vw] max-w-[640px] md:w-[560px] lg:w-[640px] h-auto select-none"
                        style={{
                            filter: "brightness(0) invert(1)",
                            opacity: 0.08,
                            mixBlendMode: "screen",
                        }}
                    />
                </div>
                <div className="relative z-10 h-full flex flex-col justify-end pb-24 md:pb-32 px-6 md:px-16 max-w-[1600px] mx-auto text-white">
                    <FadeIn delay={120} className="visible mt-6 max-w-4xl">
                        <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
                            Timeless Craftsmanship.<br />
                            <span className="italic text-white/90">Curated Since the Early 1980s.</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={260} className="visible mt-8 max-w-2xl">
                        <p className="text-base md:text-lg font-light leading-relaxed text-white/85">
                            Discover museum-worthy carpets, antique sculptures, handcrafted jewelry, luxurious cashmere and rare artisan treasures sourced from across Asia.
                        </p>
                    </FadeIn>
                    <FadeIn delay={400} className="visible mt-10 flex flex-col sm:flex-row gap-4">
                        <Link to="/collections" data-testid={HOME.heroExploreBtn} className="btn-primary group">
                            Explore Collection
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.6} />
                        </Link>
                        <button
                            type="button"
                            onClick={scrollToConsult}
                            data-testid={HOME.heroWhatsappBtn}
                            className="btn-secondary group"
                        >
                            <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
                            WhatsApp Enquiry
                        </button>
                    </FadeIn>
                </div>

                {/* Brand tag — bottom right of the hero */}
                <FadeIn className="visible absolute bottom-6 md:bottom-8 right-6 md:right-16 z-10 text-right hidden sm:block">
                    <p className="overline text-white/75 text-[10px] md:text-xs">
                        {BRAND.parent}
                    </p>
                    <div className="w-8 h-px bg-[#D4AF37] mt-2 ml-auto" />
                    <p className="text-[10px] md:text-xs tracking-[0.24em] text-white/60 mt-2 uppercase font-medium">
                        Est. {BRAND.founded}
                    </p>
                </FadeIn>

                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/70">
                    <span className="overline text-[10px]">Scroll</span>
                    <span className="block w-px h-10 bg-white/50" />
                </div>
            </section>

            {/* HERITAGE STRIP */}
            <section data-testid={HOME.heritageStrip} className="bg-[#0A0A0A] text-[#F1F1EE] px-6 md:px-12 py-20 md:py-28 border-t border-[#1A1A1A]">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
                    <FadeIn className="md:col-span-5">
                        <p className="overline text-[#D4AF37]">Heritage</p>
                        <h2 className="font-serif-display text-4xl md:text-5xl mt-6 leading-tight">
                            40+ Years of Curating Exceptional Craftsmanship.
                        </h2>
                    </FadeIn>
                    <FadeIn delay={150} className="md:col-span-7">
                        <p className="text-[#CFCFCF] font-light text-base md:text-lg leading-relaxed max-w-2xl">
                            Founded in the early 1980s, The Treasure Threads has spent decades sourcing rare handcrafted treasures from skilled artisans across Asia. Every piece tells a story of heritage, artistry and timeless luxury.
                        </p>
                    </FadeIn>
                </div>
                <div className="max-w-[1400px] mx-auto mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-y-8">
                    {HERITAGE_STATS.map((s, i) => (
                        <FadeIn
                            key={s.label}
                            delay={i * 100}
                            className={`px-4 md:px-8 ${i > 0 ? "md:border-l md:border-[#2A2A2A]" : ""} ${i === 1 ? "border-l border-[#2A2A2A] md:border-l" : ""} ${i === 3 ? "border-l border-[#2A2A2A]" : ""}`}
                        >
                            <p className="font-serif-display text-5xl md:text-6xl text-[#F8F8F6] leading-none">{s.value}</p>
                            <div className="w-6 h-px bg-[#D4AF37] mt-4" />
                            <p className="text-[11px] text-[#BBBBBB] mt-4 tracking-[0.2em] uppercase font-medium">{s.label}</p>
                        </FadeIn>
                    ))}
                </div>
            </section>

            {/* PRESENCE — hotels & boutiques across Asia */}
            <PresenceSection />

            {/* FEATURED CATEGORIES */}
            <section data-testid={HOME.featuredCategories} className="px-6 md:px-12 py-24 md:py-36 bg-[#0A0A0A]">
                <div className="max-w-[1600px] mx-auto">
                    <FadeIn className="max-w-3xl">
                        <p className="overline text-[#D4AF37]">Featured Categories</p>
                        <h2 className="font-serif-display text-4xl md:text-6xl mt-6 leading-[1.02] text-[#F1F1EE]">
                            A curator&rsquo;s selection of Asia&rsquo;s finest handwork.
                        </h2>
                    </FadeIn>
                    <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {FEATURED_CATEGORIES.map((c, i) => (
                            <FadeIn key={c.slug} delay={i * 90}>
                                <Link to={`/collections#${c.slug}`} className="block group">
                                    <div className="img-hover-wrap aspect-[4/5]">
                                        <img
                                            src={c.image}
                                            alt={c.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="mt-6 flex items-start justify-between gap-4">
                                        <div>
                                            <h3 className="font-serif-display text-2xl md:text-3xl text-[#F1F1EE] leading-tight">
                                                {c.title}
                                            </h3>
                                            <p className="mt-2 text-sm text-[#A8A8A8] font-light leading-relaxed max-w-md">
                                                {c.blurb}
                                            </p>
                                        </div>
                                        <ArrowRight className="w-5 h-5 mt-2 flex-shrink-0 text-[#F1F1EE] transition-all group-hover:translate-x-1 group-hover:text-[#D4AF37]" strokeWidth={1.4} />
                                    </div>
                                </Link>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ABOUT PREVIEW */}
            <section data-testid={HOME.aboutPreview} className="px-6 md:px-12 py-24 md:py-36 bg-[#111111]">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <FadeIn className="lg:col-span-6">
                        <div className="img-hover-wrap aspect-[4/5] lg:aspect-[3/4]">
                            <img src={IMG.artisanWork} alt="Master artisan at work" loading="lazy" className="w-full h-full object-cover" />
                        </div>
                    </FadeIn>
                    <FadeIn delay={150} className="lg:col-span-6 lg:pl-6">
                        <p className="overline text-[#D4AF37]">Our Heritage</p>
                        <h2 className="font-serif-display text-4xl md:text-6xl mt-6 leading-[1.02] text-[#F1F1EE]">
                            Four Decades of Heritage.
                        </h2>
                        <div className="hairline-gold mt-8" />
                        <p className="mt-8 text-base md:text-lg text-[#B8B8B8] font-light leading-relaxed max-w-xl">
                            Every collection inside The Treasure Threads reflects generations of craftsmanship. From antique sculptures and silk carpets to luxurious cashmere and fine jewelry, our pieces are carefully selected for clients who appreciate authenticity, rarity and timeless elegance.
                        </p>
                        <Link to="/about" className="btn-outline-dark group mt-10">
                            Discover Our Story
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.6} />
                        </Link>
                    </FadeIn>
                </div>
            </section>

            {/* SIGNATURE COLLECTIONS - masonry */}
            <section data-testid={HOME.signatureCollections} className="px-6 md:px-12 py-24 md:py-36 bg-[#0A0A0A]">
                <div className="max-w-[1600px] mx-auto">
                    <FadeIn className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div>
                            <p className="overline text-[#D4AF37]">Signature Collections</p>
                            <h2 className="font-serif-display text-4xl md:text-6xl mt-6 leading-[1.02] text-[#F1F1EE] max-w-2xl">
                                Museum-worthy pieces, presented as a private gallery.
                            </h2>
                        </div>
                        <Link to="/collections" className="hidden md:inline-block overline gold-underline text-[#F1F1EE]">
                            View All Collections
                        </Link>
                    </FadeIn>

                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 auto-rows-[220px] md:auto-rows-[260px] gap-4 md:gap-6">
                        {SIGNATURE_COLLECTIONS.map((c, i) => (
                            <FadeIn
                                key={`${c.title}-${i}`}
                                delay={i * 60}
                                className={`img-hover-wrap ${c.span}`}
                            >
                                <img src={c.image} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                                    <p className="font-serif-display text-white text-2xl leading-tight">{c.title}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                    <div className="md:hidden mt-10 text-center">
                        <Link to="/collections" className="overline gold-underline text-[#F1F1EE]">
                            View All Collections
                        </Link>
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE US */}
            <section data-testid={HOME.whyChooseUs} className="px-6 md:px-12 py-24 md:py-36 bg-[#111111] border-t border-[#1A1A1A]">
                <div className="max-w-[1400px] mx-auto">
                    <FadeIn className="text-center max-w-2xl mx-auto">
                        <p className="overline text-[#D4AF37]">Why Choose Us</p>
                        <h2 className="font-serif-display text-4xl md:text-6xl mt-6 leading-[1.02] text-[#F1F1EE]">
                            A quiet promise, kept for four decades.
                        </h2>
                        <div className="hairline-gold mx-auto mt-8" />
                    </FadeIn>
                    <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
                        {WHY_CHOOSE_US.map((it, i) => {
                            const Icon = ICONS[it.icon] || Sparkles;
                            return (
                                <FadeIn key={it.title} delay={i * 90} className="flex flex-col items-start">
                                    <div className="w-12 h-12 rounded-full border border-[#D4AF37]/60 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.2} />
                                    </div>
                                    <h3 className="font-serif-display text-2xl mt-6 text-[#F1F1EE] leading-snug">
                                        {it.title}
                                    </h3>
                                    <div className="hairline-gold mt-4" />
                                </FadeIn>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <Testimonials />

            {/* PRIVATE CONSULTATION (inline form + contact details) */}
            <PrivateConsultation />
        </main>
    );
}
